/**
 * canadianHolidays.js
 *
 * Pure utility — no React, no side effects. Compute Canadian statutory holidays
 * for any year and province so calendar views can display them without any backend calls.
 *
 * Usage:
 *   import { getHolidayMap } from '@utils/canadianHolidays';
 *   const map = getHolidayMap([2025, 2026], 'ON');
 *   const h = map['2026-01-01']; // → { name: "New Year's Day", isPaidStat: true }
 *
 * Province codes: AB BC MB NB NL NS NT NU ON PE QC SK YT
 */

// ── Date helpers ──────────────────────────────────────────────────────────────

// Format a local Date to YYYY-MM-DD without UTC-offset drift.
function toStr(date) {
  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0")
  );
}

// Return a new Date offset by n days.
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// Nth occurrence of a weekday (0=Sun…6=Sat) within a month (1-indexed).
// e.g. nthWeekday(2026, 9, 1, 1) = first Monday of September 2026.
function nthWeekday(year, month, weekday, n) {
  const first = new Date(year, month - 1, 1);
  let offset = weekday - first.getDay();
  if (offset < 0) offset += 7;
  return new Date(year, month - 1, 1 + offset + (n - 1) * 7);
}

// Last occurrence of a given weekday strictly before a reference day-of-month.
// Used for Victoria Day: last Monday before May 25.
function lastWeekdayBefore(year, month, dayOfMonth, weekday) {
  const d = new Date(year, month - 1, dayOfMonth - 1); // start one day before ref
  while (d.getDay() !== weekday) {
    d.setDate(d.getDate() - 1);
  }
  return d;
}

// Nearest Monday to a given month/day — used for NL St. Patrick's Day (Mon near Mar 17).
function nearestMonday(year, month, dayOfMonth) {
  const ref = new Date(year, month - 1, dayOfMonth);
  const dow = ref.getDay(); // 0=Sun … 6=Sat
  if (dow === 0) return addDays(ref, 1); // Sun → next Mon
  if (dow <= 4) return addDays(ref, -(dow - 1)); // Mon–Thu → back to Mon
  return addDays(ref, 8 - dow); // Fri–Sat → next Mon
}

// Easter Sunday for a year via the Anonymous Gregorian algorithm.
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// ── Core holiday rules ────────────────────────────────────────────────────────

/**
 * Returns an array of holidays for a given year and province.
 * Each entry: { dateStr: 'YYYY-MM-DD', name: string, isPaidStat: boolean }
 *
 * @param {number} year
 * @param {string|null} province - two-letter province/territory code (e.g. 'ON', 'BC')
 */
export function getHolidaysForYear(year, province) {
  const prov = (province || "").toUpperCase().trim();
  const holidays = [];

  function add(date, name, isPaidStat) {
    holidays.push({ dateStr: toStr(date), name, isPaidStat });
  }

  const easter = easterSunday(year);

  // ── Federal (applies to all provinces unless noted) ────────────────────────

  add(new Date(year, 0, 1), "New Year's Day", true);

  // Good Friday — all provinces except QC (which observes Easter Monday instead)
  if (prov !== "QC") {
    add(addDays(easter, -2), "Good Friday", true);
  }

  // Easter Monday — QC only (and federal employees, but we scope to provincial)
  if (prov === "QC") {
    add(addDays(easter, 1), "Easter Monday", true);
  }

  add(new Date(year, 6, 1), "Canada Day", true);
  add(nthWeekday(year, 9, 1, 1), "Labour Day", true); // first Monday of September

  // National Day for Truth & Reconciliation — Sept 30: federal + BC, NT, NU, YT
  if (["BC", "NT", "NU", "YT"].includes(prov)) {
    add(new Date(year, 8, 30), "National Day for Truth & Reconciliation", true);
  }

  add(new Date(year, 11, 25), "Christmas Day", true);

  // Boxing Day — Federal employees + Ontario only as a provincial stat
  if (prov === "ON") {
    add(new Date(year, 11, 26), "Boxing Day", true);
  }

  // ── Third Monday of February (Family Day family of holidays) ──────────────
  const thirdMonFeb = nthWeekday(year, 2, 1, 3);
  if (["AB", "BC", "ON", "SK", "NB"].includes(prov)) {
    add(thirdMonFeb, "Family Day", true);
  } else if (prov === "MB") {
    add(thirdMonFeb, "Louis Riel Day", true);
  } else if (prov === "PE") {
    add(thirdMonFeb, "Islander Day", true);
  } else if (prov === "NS") {
    add(thirdMonFeb, "Heritage Day", true);
  }

  // ── NL: St. Patrick's Day — nearest Monday to March 17 ──────────────────
  if (prov === "NL") {
    add(nearestMonday(year, 3, 17), "St. Patrick's Day", true);
  }

  // ── Victoria Day / National Patriots' Day — Monday before May 25 ─────────
  const dayBeforeMay25 = lastWeekdayBefore(year, 5, 25, 1); // last Monday before May 25
  if (prov === "QC") {
    add(dayBeforeMay25, "National Patriots' Day", true);
  } else {
    // All provinces and territories except QC observe Victoria Day
    add(dayBeforeMay25, "Victoria Day", true);
  }

  // ── St-Jean-Baptiste Day — June 24 (QC only) ─────────────────────────────
  if (prov === "QC") {
    add(new Date(year, 5, 24), "St-Jean-Baptiste Day", true);
  }

  // ── First Monday of August (Civic Holiday family) — not a federal stat ────
  const firstMonAug = nthWeekday(year, 8, 1, 1);
  const civicNames = {
    ON: "Civic Holiday",
    AB: "Heritage Day",
    BC: "BC Day",
    NB: "New Brunswick Day",
    SK: "Saskatchewan Day",
    NT: "Civic Holiday",
    NU: "Civic Holiday",
  };
  if (civicNames[prov]) {
    // Treated as paid by most of these provinces even though it's not a federal stat
    add(firstMonAug, civicNames[prov], false);
  }

  // ── Discovery Day — YT: 3rd Monday of August ─────────────────────────────
  if (prov === "YT") {
    add(nthWeekday(year, 8, 1, 3), "Discovery Day", true);
  }

  // ── Thanksgiving — second Monday of October ───────────────────────────────
  // Not observed in PE, NL, NS, NB
  if (!["PE", "NL", "NS", "NB"].includes(prov)) {
    add(nthWeekday(year, 10, 1, 2), "Thanksgiving", true);
  }

  // ── Remembrance Day — November 11 ────────────────────────────────────────
  // Stat in: BC AB SK MB YT NT NU NL PE   |   Not stat in: ON QC NS
  const remDay = new Date(year, 10, 11);
  if (["BC", "AB", "SK", "MB", "YT", "NT", "NU", "NL", "PE"].includes(prov)) {
    add(remDay, "Remembrance Day", true);
  } else if (!["ON", "QC", "NS"].includes(prov)) {
    // Unknown or no province: include as non-paid observance
    add(remDay, "Remembrance Day", false);
  }
  // ON, QC, NS: Remembrance Day is not a stat — intentionally omitted

  return holidays;
}

/**
 * Build a fast O(1) lookup map keyed by YYYY-MM-DD.
 * Pass multiple years to cover month-navigation across year boundaries.
 *
 * @param {number[]} years - e.g. [2025, 2026]
 * @param {string|null} province
 * @returns {{ [dateStr: string]: { name: string, isPaidStat: boolean } }}
 */
export function getHolidayMap(years, province) {
  const map = {};
  for (const year of years) {
    for (const h of getHolidaysForYear(year, province)) {
      // When two holidays coincidentally land on the same date, keep the paid one.
      if (!map[h.dateStr] || (h.isPaidStat && !map[h.dateStr].isPaidStat)) {
        map[h.dateStr] = { name: h.name, isPaidStat: h.isPaidStat };
      }
    }
  }
  return map;
}
